/* LAV TDMS — root application shell.
   Ported 1:1 from the prototype's TDMS.html root <App> script (window.APP). */
import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { PageHead, EmptyState, Spinner, ToastHost, DensityProvider, isNarrow } from '@/ui'
import { Login, ProjectSelect } from '@/screens/pt/Auth'
import { Shell } from '@/screens/pt/Shell'
import { Inbox } from '@/screens/pt/Inbox'
import { Dashboard } from '@/screens/pt/Dashboard'
import { MdrRegister, VdrRegister } from '@/screens/pt/Registers'
import { Transmittals, Review } from '@/screens/pt/Transmittals'
import { CrsList } from '@/screens/pt/Comments'
import { DocumentCard } from '@/screens/pt/DocumentCard'
import { TrmCard, MdrCard, VdrCard } from '@/screens/pt/Cards'
import { HelpPanel } from '@/screens/pt/Help'
import { ContractorInbox, ContractorDocs, ContractorTrm, ContractorCrs } from '@/screens/pt/Contractor'
import { DocumentViewer } from '@/screens/pt/Viewer'
import { useSub } from '@/data/queries'
import { setActiveProject } from '@/data/api'
import { queryClient } from '@/main'
import { useSession } from '@/auth/useSession'
import { signIn, signOut } from '@/auth/oidc'
import { Reports, AuditLog, Admin, Vendors, Search, MdrDocs } from '@/screens/pt/Admin'
import { Handover } from '@/screens/pt/Handover'
import { Progress } from '@/screens/pt/Progress'
import { t, type Lang } from '@/i18n/lang'

type Theme = 'light' | 'dark'
type Stage = 'login' | 'projects' | 'app'
type Persona = 'owner' | 'contractor'

const LS = 'tdms_state_v2'

interface SavedState {
  stage?: Stage
  theme?: Theme
  lang?: Lang
  railed?: boolean
  compact?: boolean
  persona?: Persona
  projectId?: string
}

/* Экран, карточка и разбор конкретной записи живут в адресе, а не в состоянии
   и не в localStorage. Раньше на ссылку нельзя было сослаться, кнопка «назад»
   выходила из приложения целиком, а фокус между экранами передавался отметкой
   на window.

   Путь — это пункт навигации: /mdr, /crs, /review. Второй сегмент, где он
   осмыслен, указывает запись: /review/TRM-0042.

   Карточки и просмотрщик — параметры запроса (?doc=…, ?viewer=…), потому что
   это наложение поверх экрана, а не другой экран: закрытие обязано вернуть
   туда же, откуда открыли, и «назад» делает ровно это. */
const OVERLAYS = ['doc', 'viewer', 'trm', 'mdr', 'vdr'] as const
type Overlay = (typeof OVERLAYS)[number]

function Placeholder({ title }: { title: string }) {
  return (
    <div style={{ minHeight: '100%', background: 'var(--surface-page)' }}>
      <PageHead title={title} />
      <EmptyState icon="hammer" title="Экран в разработке" text="Этот раздел будет добавлен в следующей итерации прототипа." />
    </div>
  )
}

export function App() {
  const saved: SavedState = (() => {
    try {
      return (JSON.parse(localStorage.getItem(LS) || '{}') as SavedState) || {}
    } catch {
      return {}
    }
  })()
  const [stage, setStage] = useState<Stage>(saved.stage || 'login') // login | projects | app
  const [theme, setTheme] = useState<Theme>(saved.theme || 'light')
  const [lang, setLang] = useState<Lang>(saved.lang || 'ru')
  /* Боковое меню, свёрнутое в полосу иконок. Такая же настройка рабочего
     места, как тема и язык, поэтому лежит рядом с ними и переживает
     перезагрузку.

     Узкое окно решает только ПЕРВЫЙ вход: на 1366 меню занимает 224 px из
     1142 полезных, и по умолчанию их лучше отдать работе. Дальше значение
     берётся из хранилища, каким бы оно ни было, — иначе размер окна каждый
     раз переспорит человека, который свернул или развернул меню сам. */
  const [railed, setRailed] = useState<boolean>(saved.railed ?? isNarrow())
  /* Справка — не наложение над записью, а чтение: адрес ею не занимается, и
     закрытие не должно уводить с экрана, на котором её открыли. */
  const [help, setHelp] = useState(false)
  /* Плотность таблиц — тем же правилом: на узком окне строки по умолчанию
     плотные (на 768 это восемь лишних строк), но выбор человека сильнее. */
  const [compact, setCompact] = useState<boolean>(saved.compact ?? isNarrow())
  const density = useMemo(() => ({ compact, setCompact }), [compact])
  /* Активный проект. Выбирать можно только из тех, что named в токене: сервер
     принимает заголовок X-Project лишь при вхождении в это утверждение —
     изоляция остаётся токенной. Значение переживает перезагрузку в
     localStorage и проверяется по списку, как только сессия восстановлена. */
  const [projectId, setProjectId] = useState<string>(saved.projectId || '')

  // Кто пользователь — решает токен, а не выбор в интерфейсе. Наличие vendor
  // и есть признак подрядчика; подменить его из браузера нельзя.
  const auth = useSession()
  const session = auth.status === 'authenticated' ? auth.session : null
  const persona: Persona = session?.vendor ? 'contractor' : 'owner'
  const vendor = session?.vendor ?? ''

  const location = useLocation()
  const navigate = useNavigate()

  /* Переключение проекта — единственная точка смены. Порядок обязателен:
     сначала заголовок, потом сброс кеша, иначе рефетчи уйдут со старым
     проектом и наполнят кеш чужими данными под видом нового. Кеш сбрасывается
     целиком: справочники живут вечно (staleTime Infinity), а реестры, счётчики
     и /me принадлежат проекту. Адрес чистится от наложений и записи — карточка
     чужого проекта дала бы 404 сразу после переключения. */
  const pickProject = (id: string) => {
    if (!id || id === projectId) return
    setActiveProject(id)
    setProjectId(id)
    void queryClient.cancelQueries()
    queryClient.clear()
    navigate('/inbox')
  }
  const [params, setParams] = useSearchParams()
  const [screenId = 'inbox', focus] = location.pathname.replace(/^\/+|\/+$/g, '').split('/')
  const active = screenId || 'inbox'
  const onNav = useCallback((to: string) => navigate('/' + to.replace(/^\/+/, '')), [navigate])
  /* Готовый отбор по контрагенту: с экрана организаций проваливаются в его
     документы, переписку и замечания. Живёт в адресе, а не в состоянии, —
     ссылку на «документы ГидроМаша» можно дать в письме, и «назад» её снимет. */
  const vendorFilter = params.get('vendor')

  /* Открытие наложения — новая запись в истории: «назад» обязано закрывать
     карточку, а не уводить на прошлый экран. Переход с карточки на карточку
     (документ → просмотрщик) историю не растит: иначе «назад» шло бы через
     цепочку промежуточных состояний вместо возврата к списку. */
  const overlay = (kind: Overlay) => params.get(kind)

  /* Открывающие наложение обработчики обязаны быть неизменными по личности:
     иначе элемент экрана пересоздаётся при каждом открытии карточки и React
     перерисовывает реестр целиком. Измерено на стенде: клик по строке на
     экране в сто строк давал задачу в 69 мс, и панель трогалась только на
     123-й миллисекунде — это и читалось рывком. На лёгком экране задачи не
     было вовсе, и панель шла с 41-й.

     Отсюда ссылка на текущие параметры: замыкаться на них значит менять
     личность обработчика на каждый переход. */
  const paramsRef = useRef(params)
  paramsRef.current = params
  const setParamsRef = useRef(setParams)
  setParamsRef.current = setParams

  const setOverlay = useCallback((kind: Overlay, value: string | null, at?: string,
                                 spot?: { page?: number; rev?: string; ver?: number }) => {
    const current = paramsRef.current
    const next = new URLSearchParams(current)
    OVERLAYS.forEach((k) => next.delete(k))
    /* Замечание и лист, на которые надо открыть документ, живут рядом с
       наложением и снимаются вместе с ним: иначе следующее открытие того же
       документа унесло бы к прошлой подсветке. */
    next.delete('crs')
    next.delete('page')
    next.delete('rev')
    next.delete('ver')
    if (value) next.set(kind, value)
    if (value && at) next.set('crs', at)
    if (value && spot?.page) next.set('page', String(spot.page))
    if (value && spot?.rev) next.set('rev', spot.rev)
    if (value && spot?.ver) next.set('ver', String(spot.ver))
    setParamsRef.current(next, { replace: OVERLAYS.some((k) => current.get(k) != null) })
  }, [])
  const drawerDoc = overlay('doc')
  const viewerDoc = overlay('viewer')
  const viewerCrs = params.get('crs')
  const viewerPage = Number(params.get('page')) || undefined
  const viewerRev = params.get('rev') ?? undefined
  const viewerVer = Number(params.get('ver')) || undefined
  const trmCard = overlay('trm')
  const mdrCard = overlay('mdr')
  const vdrCard = overlay('vdr')

  useEffect(() => {
    document.documentElement.dataset.theme = theme
  }, [theme])

  // После входа стадия login больше не имеет смысла: подрядчик попадает сразу
  // в свой портал, сотрудник — к выбору проекта.
  useEffect(() => {
    if (session && stage === 'login') setStage(session.vendor ? 'app' : 'projects')
  }, [session, stage])

  /* Токен авторитетен в том, ЧТО разрешено: из localStorage принимается только
     проект, названный в утверждении. Иначе после отзыва доступа вкладка
     продолжала бы слать заголовок, который сервер отобьёт 403.
     setActiveProject вызывается синхронно с проверкой — запросы стартуют по
     готовности сессии, и первый залп обязан уйти уже с верным заголовком. */
  useEffect(() => {
    const allowed = session?.projects ?? []
    if (allowed.length === 0) return
    const effective = allowed.includes(projectId) ? projectId : allowed[0]!
    setActiveProject(effective)
    if (effective !== projectId) setProjectId(effective)
  }, [session, projectId])

  useEffect(() => {
    localStorage.setItem(LS, JSON.stringify({ stage, theme, lang, railed, compact, persona, projectId }))
  }, [stage, theme, lang, railed, compact, persona, projectId])

  const isContractor = persona === 'contractor'

  const openDoc = useCallback((no: string) => setOverlay('doc', no), [setOverlay])
  const openViewer = useCallback((no: string, crs?: string) => setOverlay('viewer', no, crs), [setOverlay])
  /** Открыть чертёж на конкретном листе — так приходят находки поиска по файлам. */
  const openViewerPage = useCallback((no: string, at?: { page?: number; rev?: string; ver?: number }) =>
    setOverlay('viewer', no, undefined, at), [setOverlay])
  const openTrm = useCallback((id: string) => setOverlay('trm', id), [setOverlay])
  const openMdr = useCallback((id: string) => setOverlay('mdr', id), [setOverlay])
  const openVdr = useCallback((id: string) => setOverlay('vdr', id), [setOverlay])
  const closeOverlay = useCallback(() => setOverlay('doc', null), [setOverlay])

  /* Элемент экрана пересобирается только когда меняется сам экран, язык или
     подсвеченная запись. Открытие карточки к нему отношения не имеет — и
     React пропускает поддерево целиком, вместо того чтобы перерисовывать
     таблицу под панелью. */
  const screen = useMemo<ReactNode>(() => {
    let screen: ReactNode
    if (isContractor) {
      if (active === 'inbox')
        screen = (
          <ContractorInbox
            lang={lang}
            vendor={vendor}
            onOpenDoc={openDoc}
            onAnswer={(id) => onNav('ccrs/' + id)}
            onSubmitDoc={(no) => openDoc(no)}
          />
        )
      else if (active === 'cmdr') screen = <MdrRegister lang={lang} onOpenDoc={openDoc} onOpenViewer={openViewer} onOpenMdr={openMdr} />
      else if (active === 'cdocs') screen = <ContractorDocs lang={lang} vendor={vendor} onOpenDoc={openDoc} />
      else if (active === 'ctrm') screen = <ContractorTrm lang={lang} vendor={vendor} onOpenTrm={openTrm} />
      else if (active === 'ccrs') screen = <ContractorCrs lang={lang} vendor={vendor} focus={focus} />
      else
        screen = (
          <ContractorInbox lang={lang} vendor={vendor} onOpenDoc={openDoc} onAnswer={(id) => onNav('ccrs/' + id)} onSubmitDoc={(no) => openDoc(no)} />
        )
    } else if (active === 'inbox') screen = <Inbox lang={lang} onNav={onNav} onOpenDoc={openDoc} onOpenViewer={openViewer} onOpenTrm={openTrm} />
    else if (active === 'dashboard') screen = <Dashboard lang={lang} onNav={onNav} onOpenDoc={openDoc} />
    else if (active === 'mdr') screen = <MdrRegister lang={lang} onOpenDoc={openDoc} onOpenViewer={openViewer} onOpenMdr={openMdr} />
    else if (active === 'mdrdocs') screen = <MdrDocs lang={lang} vendor={vendorFilter} onOpenDoc={openDoc} onOpenViewer={openViewer} />
    else if (active === 'vdr') screen = <VdrRegister lang={lang} onOpenDoc={openDoc} onOpenViewer={openViewer} onOpenVdr={openVdr} />
    else if (active === 'trm') screen = <Transmittals lang={lang} vendor={vendorFilter} onOpenDoc={openDoc} onOpenTrm={openTrm} />
    else if (active === 'handover') screen = <Handover lang={lang} />
    else if (active === 'progress') screen = <Progress lang={lang} />
    else if (active === 'review') screen = <Review lang={lang} focus={focus} onOpenViewer={openViewer} onOpenDoc={openDoc}
      onOpenTrm={openTrm} onOpenCrs={(code) => onNav('crs/' + encodeURIComponent(code))} />
    else if (active === 'crs') screen = <CrsList lang={lang} focus={focus} vendor={vendorFilter} onOpenViewer={openViewer} />
    else if (active === 'reports') screen = <Reports lang={lang} />
    else if (active === 'search') screen = <Search lang={lang} onOpenDoc={openDoc} onOpenViewer={openViewerPage} />
    else if (active === 'vendors') screen = <Vendors lang={lang} onNav={onNav} />
    else if (active === 'audit') screen = <AuditLog lang={lang} />
    else if (active === 'admin') screen = <Admin lang={lang} focus={focus} onOpenDoc={openDoc} />
    else screen = <Placeholder title="Мои действия" />
    return screen
  }, [active, lang, focus, isContractor, vendor, vendorFilter,
    onNav, openDoc, openViewer, openViewerPage, openTrm, openMdr, openVdr])

  /* Числа на пунктах навигации приходят одним запросом. Раньше каждое
     пересчитывалось в браузере по всему набору на каждый рендер — против
     сервера так нельзя: у клиента есть только страница. */
  // Запрос уходит только после восстановления сессии: без токена он получил бы
  // 401 ещё до того, как приложение узнало, кто вошёл.
  const navCounters = useSub<Record<string, number>>(
    auth.status === 'authenticated' ? '/nav-counters' : null,
  ).data ?? {}

  /* У подрядчика те же числа, только под своими именами пунктов: сервер уже
     посчитал их в границах его вендора. Считать их здесь по всему набору,
     как раньше, значит сначала получить в браузер чужие документы. */
  const counts: Record<string, number | undefined> = isContractor
    ? {
        inbox: navCounters.inbox,
        cmdr: navCounters.mdr,
        cdocs: navCounters.docs,
        ctrm: navCounters.trm,
        ccrs: navCounters.crs,
      }
    : navCounters

  if (auth.status === 'loading') {
    return (
      <div data-theme={theme} style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--surface-page)', font: 'var(--font-ui)', color: 'var(--text-subtle)' }}>
        {/* Спиннер рядом с текстом: неподвижная надпись неотличима от
            зависания, а вращение — единственный видимый признак, что запрос
            ещё идёт. Внешний грид центрирует одну ячейку — внутрь встаёт флекс. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Один компонент на все «Загрузка…»: голый span расходился бы с
              эталоном при первой же правке .spinner. */}
          <Spinner size={13} />{t(lang, 'Проверка сессии…', 'Checking session…')}
        </div>
      </div>
    )
  }
  if (auth.status === 'anonymous') {
    return <Login lang={lang} onLang={setLang} theme={theme} onTheme={setTheme} onSignIn={() => void signIn()} error={auth.error} />
  }
  if (stage === 'projects') {
    return (
      <ProjectSelect
        lang={lang}
        theme={theme}
        allowed={session?.projects ?? []}
        onOpen={(p) => {
          /* Тот же путь, что у переключателя в топбаре: смена проекта обязана
             сбрасывать кеш, даже когда приходит с экрана выбора. Если пришли по
             ссылке на конкретную запись и проект не меняется — адрес сохраняем. */
          if (p.id !== projectId) pickProject(p.id)
          else if (location.pathname === '/') navigate('/inbox')
          setStage('app')
        }}
        onBack={() => void signOut()}
      />
    )
  }


  return (
    <DensityProvider value={density}>
      <Shell
        active={active}
        onNav={onNav}
        theme={theme}
        onTheme={(t) => setTheme(t as Theme)}
        lang={lang}
        onLang={(l) => setLang(l as Lang)}
        railed={railed}
        onRailed={setRailed}
        onHelp={() => setHelp(true)}
        persona={persona}
        vendor={vendor}
        onPickProject={pickProject}
        allowedProjects={session?.projects ?? []}
        onSwitchProject={() => {
          /* У подрядчика проект один и он в токене: «сменить учётную запись»
             означает выйти. Прежний код возвращал стадию login, откуда эффект
             тут же возвращал обратно в приложение — кнопка не делала ничего. */
          if (isContractor) void signOut()
          else setStage('projects')
        }}
        onSignOut={() => void signOut()}
        counts={counts}
      >
        {screen}
      </Shell>
      {trmCard ? (
        <div data-theme={theme}>
          <TrmCard
            id={trmCard}
            lang={lang}
            onClose={closeOverlay}
            onOpenDoc={openDoc}
            onOpenReview={() => {
              closeOverlay()
              onNav('review')
            }}
            onOpenCrs={(code) => {
              closeOverlay()
              onNav((isContractor ? 'ccrs/' : 'crs/') + encodeURIComponent(code))
            }}
          />
        </div>
      ) : null}
      <HelpPanel open={help} onClose={() => setHelp(false)} lang={lang} />
      {mdrCard ? (
        <div data-theme={theme}>
          <MdrCard
            id={mdrCard}
            lang={lang}
            onClose={closeOverlay}
            onOpenDoc={openDoc}
            onOpenRegister={() => {
              closeOverlay()
              onNav('mdr')
            }}
          />
        </div>
      ) : null}
      {vdrCard ? (
        <div data-theme={theme}>
          <VdrCard
            id={vdrCard}
            lang={lang}
            onClose={closeOverlay}
            onOpenDoc={openDoc}
            onOpenRegister={() => {
              closeOverlay()
              onNav('vdr')
            }}
          />
        </div>
      ) : null}
      {drawerDoc ? (
        <div data-theme={theme}>
          <DocumentCard no={drawerDoc} lang={lang} onClose={closeOverlay} onOpenViewer={openViewer}
            onOpenTrm={openTrm}
            /* Замечание открывается своим экраном — тем же путём, что ссылка из
               уведомления: экран найдёт лист по коду и прокрутит к строке.
               Сторона решает, чьим экраном: у подрядчика раздел crs закрыт. */
            onOpenCrs={(code) => onNav((isContractor ? 'ccrs/' : 'crs/') + encodeURIComponent(code))} />
        </div>
      ) : null}
      {viewerDoc ? (
        <div data-theme={theme} style={{ position: 'fixed', inset: 0, zIndex: 950 }}>
          <DocumentViewer no={viewerDoc} crs={viewerCrs ?? undefined} page={viewerPage}
                          rev={viewerRev} ver={viewerVer} lang={lang} onBack={closeOverlay} />
        </div>
      ) : null}
      <ToastHost />
    </DensityProvider>
  )
}
